import { forwardRef, useEffect, useRef, useState } from 'react';
import { getLogger } from '@sas/nova-core/logging';
import { ErrorStatus, WarningStatus } from '@sas/react-svg';
import cx from 'classnames';
import { AnimatePresence, motion } from 'framer-motion';
import { useReactDndConstructs as useDragAndDropHooks } from '../../dnd/ReactDndContext';
import { useFeatureFlag } from '../../hooks/useFeatureFlag';
import { createUseHtmlId } from '../../hooks/useHtmlId';
import { useMounted } from '../../hooks/useMounted';
import useOnFocusOut from '../../hooks/useOnFocusOut';
import { createUseTextLocalizer } from '../../locale/createUseTextLocalizer';
import { useFormatter } from '../../locale/formatting';
import { getDndDesignTokens } from '../../style/reactDnd/DndStyleTokens';
import { renderInlineCssVars, useDesignTokens } from '../../theme';
import { setRef } from '../../util/ref-util';
import { postfixTestId } from '../../util/testId';
import { Button, ButtonVariant } from '../Button';
import { useDirectSpeech } from '../DirectSpeech/DirectSpeechProvider';
import { Icon } from '../Icon';
import { BaseInput, getInputDesignTokens } from '../Input/__internal__/BaseInput';
import { MessagePopover } from '../Input/__internal__/MessagePopover';
import { Flow } from '../Layouts/Flow';
import { GlobalSettingsKeys } from '../Settings/GlobalSettingsKeys';
import { useGetGlobalSetting } from '../Settings/GlobalSettingsProvider';
import { TokenDragPreview } from '../Token/__internal__/TokenDragPreview';
import { Tokenizer } from '../Tokenizer';
import { useRequiredTokenInputValidation } from './__internal__';
import bundle from './NovaComponents-TokenInput-gui-icu.properties';
import cn from './TokenInput.css.json';
import './TokenInput.css';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export const TokenDragType = 'NOVA_TOKEN';
const useTextLocalizer = createUseTextLocalizer(bundle);
const useTokenInputDndPrefixId = createUseHtmlId('TokenInput-dndPrefix');
const logger = getLogger('TokenInput');
/**
 * This hook can be used to manage the state for a token input component.
 * This is the preferred way to set TokenInput's required props.
 * @example
 * ```tsx
 * const tokenInputProps = useTokenInputState(initialTokens);
 * // ...
 * return <TokenInput {...tokenInputProps} />
 * ```
 */
export const useTokenInputState = (initialTokens, onCreateToken) => {
  const [tokens, setTokens] = useState(initialTokens);
  const [inputValue, setInputValue] = useState('');
  const onAdd = (value, icon) => {
    const newToken = onCreateToken?.(value, icon) ?? {
      value,
      icon
    };
    setTokens(prevTokens => [...prevTokens, newToken]);
  };
  const onRemove = toRemoveToken => {
    setTokens(prevTokens => prevTokens.filter(token => token !== toRemoveToken));
  };
  return {
    tokens,
    setTokens,
    onAdd,
    onRemove,
    inputValue,
    onInputChange: ({
      value
    }) => setInputValue(value)
  };
};
export const TokenInput = /*#__PURE__*/forwardRef(function TokenInput({
  allowDrop = false,
  ...props
}, ref) {
  const localize = useTextLocalizer();
  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const isBtnPressed = useRef(false);
  const [isHovered, setIsHovered] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [hasReceivedFocus, setHasReceivedFocus] = useState(false);
  const [isInputFocused, setIsInputFocused] = useState(false);
  const directSpeech = useDirectSpeech();
  const [duplicateValue, setDuplicateValue] = useState('');
  const [showUncommittedError, setShowUncommittedError] = useState(false);
  const [showNotMatchSuggestionError, setShowNotMatchSuggestionError] = useState(false);
  const [showBlankValueError, setShowBlankValueError] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState();
  const formatter = useFormatter();
  const maxNumTokensReached = props.maxNumTokens !== undefined && props.tokens.length >= props.maxNumTokens;
  const showMaxNumTokensReachedError = maxNumTokensReached && props.inputValue.length > 0;
  const mountedRef = useMounted();
  const {
    useDrop
  } = useDragAndDropHooks();
  const defaultDndPrefix = useTokenInputDndPrefixId();
  const dndPrefix = props.dndPrefix ?? defaultDndPrefix;
  const tokens = props.tokens.map(token => {
    const tokenizerToken = token; // use the same object because it is used for deleting tokens
    if (props.menuSections || tokenizerToken.menuSections !== undefined) {
      tokenizerToken.menuSections = props.menuSections;
    }
    if (props.readOnly || tokenizerToken.readOnly !== undefined) {
      tokenizerToken.readOnly = props.readOnly;
    }
    if (props.allowDrag) {
      tokenizerToken.allowDrag = tokenizerToken.allowDrag ?? props.allowDrag;
      tokenizerToken.onDidDrop = () => {
        setFocusedIndex(undefined);
      };
    }
    if (props.dndPrefix || tokenizerToken.dndPrefix !== undefined || tokenizerToken.allowDrag) {
      tokenizerToken.dndPrefix = dndPrefix;
    }
    return tokenizerToken;
  });
  const onRemoveTokens = indices => {
    if (tokens.length === 0 || indices.length === 0) return;

    // If no token left after removing, set the focus on the input
    if (tokens.length === 1) {
      inputRef.current?.focus();
    }
    // The Tokenizer in TokenInput only removes one token at a time,
    // so assume the indices has length of 1
    const tokenToRemove = tokens[indices[0]];
    props.onChange?.(props.tokens.filter(token => token !== tokenToRemove));
    props.onRemove?.(tokenToRemove);
  };
  const {
    onFocus: onGridFocus,
    onBlur: onGridBlur,
    status: requiredStatus
  } = useRequiredTokenInputValidation({
    required: props.required,
    tokenCount: props.tokens.length,
    inputValue: props.inputValue,
    requiredErrorMessage: props.requiredErrorMessage
  });
  const getDefaultStatus = () => {
    if (showMaxNumTokensReachedError && props.maxNumTokens !== undefined) {
      return {
        type: 'alert',
        message: props.maxNumTokensReachedErrorMessage ?? (props.maxNumTokens === 1 ? localize('noMoreThanOne.error.msg') : localize('noMoreThanNum.error.msg', {
          maximum: formatter?.formatNumeric(props.maxNumTokens) ?? props.maxNumTokens.toString()
        }))
      };
    }
    if (showBlankValueError) {
      return {
        type: 'alert',
        message: props.blankValueErrorMessage ?? localize('blankValue.error.msg')
      };
    }
    if (duplicateValue) {
      return {
        type: 'alert',
        message: props.duplicateWarningMessage?.(duplicateValue) ?? localize('duplicateToken.error.msg', {
          name: duplicateValue
        })
      };
    }
    if (showNotMatchSuggestionError) {
      return {
        type: 'alert',
        message: props.mustMatchSuggestionErrorMessage ?? localize('mustMatchSuggestion.error.msg')
      };
    }
    if (showUncommittedError) {
      return {
        type: 'alert',
        message: props.uncommittedValueErrorMessage ?? localize('uncommittedValue.error.msg')
      };
    }
    if (requiredStatus) {
      return requiredStatus;
    }
    return undefined;
  };
  const status = props.status ?? getDefaultStatus();
  useEffect(() => {
    if (props.autofocus) {
      inputRef.current?.focus();
    }
  }, [props.autofocus]);
  useEffect(() => {
    // Clear the error/warning message when input's value changes
    setDuplicateValue('');
    setShowBlankValueError(false);
    setShowUncommittedError(false);
    setShowNotMatchSuggestionError(false);
  }, [props.inputValue]);

  // Feature flag for 'New Token Values'
  const isNewTokenValueFlagEnabled = useFeatureFlag('NOVACOMMONS-22288_ThemeModernization_NewTokenValues');
  const dt = useDesignTokens();

  // Outline Focus Indicator
  const style = {
    ...renderInlineCssVars(getTokenInputThemeTokens(dt, {
      status: status?.type,
      hasButton: !!props.buttonProps,
      isNewTokenValueFlagEnabled,
      disabled: props.disabled
    }), 'token-input'),
    ...renderInlineCssVars({
      outline: dt.themeVariant !== 'high-contrast' ? 'none' : 'var(--focus-light-bg)',
      'outline-offset': 'calc(-1 * var(--focus-width) - 2px)'
    }, 'token-input-focus')
  };
  const onMouseEnter = () => {
    setIsHovered(true);
  };
  const onMouseLeave = () => {
    setIsHovered(false);
  };
  const stateMessage = hasReceivedFocus && status ? `${props.ariaLabel ?? ''}, ${localize(status.type === 'alert' ? 'errorState.aria' : 'warningState.aria')}` : undefined;
  const messageToAnnouce = isFocused ? status?.message : stateMessage;
  useEffect(() => {
    if (messageToAnnouce) {
      directSpeech?.notify?.(messageToAnnouce);
    }
  }, [directSpeech, messageToAnnouce]);
  useEffect(() => {
    if (props.maxNumTokens && props.tokens.length > props.maxNumTokens) {
      logger.warn(`The maximum number of items is ${props.maxNumTokens}. The rest of the items could not be loaded.`);
    }
  }, [props.maxNumTokens, props.tokens.length]);
  const {
    onFocus: onInputFocus,
    onBlur: onInputBlur
  } = useOnFocusOut(() => {
    if (!mountedRef.current) return;
    setIsInputFocused(false);
    if (props.inputValue.length === 0) return;
    if (props.inputValue.trim().length === 0) {
      setShowBlankValueError(true);
    } else {
      setShowUncommittedError(true);
    }
  });
  const {
    onFocus,
    onBlur
  } = useOnFocusOut(() => {
    if (!mountedRef.current) return;
    setIsFocused(false);
    setHasReceivedFocus(true);
  });
  const isDuplicate = props.isDuplicate ?? (value => tokens.some(token => token.value === value));
  const suggestionItems = props.preventDuplicates ? props.suggestionItems?.filter(({
    text
  }) => text && !isDuplicate(text)) : props.suggestionItems;
  const canDrop = item => allowDrop && item.dndPrefix !== dndPrefix && (!props.allowDropDndPrefixes || props.allowDropDndPrefixes.includes(item.dndPrefix));
  const [{
    isOverTokenInputDropZone
  }, tokenInputDropZoneConnect] = useDrop({
    accept: [TokenDragType],
    canDrop,
    collect(monitor) {
      return {
        isOverTokenInputDropZone: monitor.isOver() && canDrop(monitor.getItem())
      };
    },
    drop: (item, monitor) => {
      if (!monitor.didDrop() && canDrop(item)) {
        setFocusedIndex(props.tokens.length);
        props.onDrop?.({
          droppedItem: item,
          monitor
        });
      }
    }
  });
  tokenInputDropZoneConnect(containerRef);
  const dndTokens = getDndDesignTokens(dt);
  const shouldPlayAnimation = useGetGlobalSetting(GlobalSettingsKeys.PLAY_VISUAL_EFFECTS) === 'true';
  const iconAnimation = !shouldPlayAnimation ? undefined : {
    closed: {
      opacity: 0
    },
    open: {
      opacity: 1,
      x: 0,
      y: 0,
      transition: {
        type: 'tween',
        duration: 0.3,
        ease: [0, 0.5, 0.2, 1]
      }
    },
    exit: {
      opacity: 0,
      transition: {
        type: 'tween',
        duration: 0.3,
        ease: [0.5, 0, 0.8, 1]
      }
    }
  };
  return /*#__PURE__*/_jsxs(Flow, {
    role: "grid",
    orientation: "horizontal",
    spacing: "none",
    "aria-label": props.ariaLabel,
    "aria-labelledby": props.ariaLabelledBy,
    "aria-describedby": props.ariaDescribedBy,
    style: {
      ...style,
      ...dndTokens,
      ...props.style
    },
    className: cx(cn.outerContainer, isNewTokenValueFlagEnabled && cn.featureFlagEnabled, props.className, props.readOnly && cn.readOnly),
    onFocus: onGridFocus,
    onBlur: e => {
      /*
        The logic for required validation was updated to show the error message even when the selector (the user group dialog) is open.
        In order to prevent the error message from showing when the user clicks on the selector, we need to check if the button is pressed.
        So, if the button is pressed, we won't call the onGridBlur function, which will prevent the validation.
        This logic is similar to the one used by CompositeInput to prevent the validations. This is done for the multiple selection user group selector.
      */
      if (isBtnPressed.current) {
        isBtnPressed.current = false;
      } else {
        onGridBlur?.(e);
      }
    },
    children: [/*#__PURE__*/_jsxs("div", {
      ref: containerRef,
      className: cx(cn.container, isInputFocused && cn.focusVisible,
      // Always save space for the icon even when it is not visible to avoid re-layout of tokens when focused
      status && cn.hasStatusIcon, isOverTokenInputDropZone && cn.dragOver),
      onMouseEnter: onMouseEnter,
      onMouseLeave: onMouseLeave,
      onBlur: onBlur,
      onFocus: () => {
        setIsFocused(true);
        onFocus();
      },
      onClick: e => {
        const tokenizerDiv = e.currentTarget.children[0];
        if (e.target === containerRef.current || e.target === tokenizerDiv) {
          // Move focus to the input if the user clicks the blank area
          inputRef.current?.focus();
        }
      },
      children: [/*#__PURE__*/_jsx(Tokenizer, {
        testId: postfixTestId(props.testId, 'tokenizer'),
        tokens: props.maxNumTokens ? tokens.slice(0, props.maxNumTokens) : tokens,
        onRemoveTokens: onRemoveTokens,
        wrap: true,
        role: "",
        className: cx(cn.tokenizer, props.tokenizerClassName),
        style: props.tokenizerStyle,
        focusedIndexState: [focusedIndex, setFocusedIndex],
        maxLines: props.maxLines,
        disabled: props.disabled,
        children: props.readOnly ? null : /*#__PURE__*/_jsx("div", {
          role: "gridcell",
          className: cn.inputFieldContainer,
          children: /*#__PURE__*/_jsx(BaseInput, {
            id: props.id,
            testId: postfixTestId(props.testId, 'input'),
            className: cn.inputField,
            disabled: props.disabled
            // need to set so that tokens are not skipped
            // when tabbing to TokenInput control
            ,
            tabindex: props.disabled ? 0 : undefined,
            style: {
              '--input-bg': 'trasparent',
              '--input-hover-bg': 'transparent',
              '--input-focus-bg': 'transparent'
            },
            ref: element => {
              inputRef.current = element;
              setRef(ref, element);
            },
            value: props.inputValue,
            filterType: props.inputFilterType ?? 'contains'
            // Do not show the suggestion list if the tokens maximum is reached
            // to avoid showing two popovers: inline error message and suggestion list
            ,
            suggestionItems: maxNumTokensReached ? undefined : suggestionItems,
            hideBorder: true,
            ariaLabel: props.inputAriaLabel ?? localize('input.default.aria'),
            ariaInvalid: status?.type === 'alert',
            placeholder: tokens.length === 0 ? props.inputPlaceholder : undefined,
            onChange: props.onInputChange,
            onCommit: async ({
              value,
              reason
            }) => {
              if (value.length === 0 || reason === 'blur' || maxNumTokensReached || props.disabled) return;
              const trimmedValue = value.trim();

              // Check for blank values
              if (trimmedValue.length === 0) {
                setShowBlankValueError(true);
                return;
              }

              // Check for duplicate values
              if (props.preventDuplicates && isDuplicate(trimmedValue)) {
                setDuplicateValue(trimmedValue);
                return;
              }
              const suggestionItem = props.suggestionItems?.find(({
                text
              }) => text === trimmedValue);

              // Check for values matching suggestions
              if (props.mustMatchSuggestion && reason !== 'suggestion' && !(reason === 'enter' && suggestionItem !== undefined)) {
                setShowNotMatchSuggestionError(true);
                return;
              }
              const isValid = props.isValueValid ? await props.isValueValid(trimmedValue) : true;
              if (!isValid) return;
              props.onChange?.([...props.tokens, {
                value: trimmedValue
              }]);
              props.onAdd?.(trimmedValue, getIconProps(suggestionItem));
              props.onInputChange({
                value: '',
                reason: 'onAdd'
              });
              directSpeech?.notify(localize('tokenAdded.aria', {
                label: trimmedValue
              }));
            },
            onBlur: onInputBlur,
            onFocus: () => {
              setIsInputFocused(true);
              onInputFocus();
            },
            commitOnEnterInDialog: true,
            required: props.required,
            ...props.inputProps
          })
        })
      }), /*#__PURE__*/_jsx(AnimatePresence, {
        children: !isFocused && status ? /*#__PURE__*/_jsx(motion.div, {
          className: cn.inputIcon,
          onMouseEnter: onMouseEnter,
          onMouseLeave: onMouseLeave,
          variants: iconAnimation,
          initial: "closed",
          animate: "open",
          exit: "exit",
          children: /*#__PURE__*/_jsx(Icon, {
            icon: status.type === 'alert' ? ErrorStatus : WarningStatus,
            color: status.type,
            ariaHidden: true
          })
        }) : null
      }), status ? /*#__PURE__*/_jsx(MessagePopover, {
        status: status.type,
        referenceElementRef: containerRef,
        message: status.message,
        isOpen: isFocused || isHovered,
        testId: postfixTestId(props.testId, 'messagePopper')
      }) : null]
    }), props.buttonProps ? /*#__PURE__*/_jsx("div", {
      role: "row",
      children: /*#__PURE__*/_jsx("div", {
        role: "gridcell",
        children: /*#__PURE__*/_jsx(Button, {
          variant: ButtonVariant.Light,
          ...props.buttonProps,
          disabled: props.disabled,
          onPress: e => {
            isBtnPressed.current = true;
            props.buttonProps?.onPress?.(e);
          },
          className: cx(props.buttonProps.className, cn.button)
        })
      })
    }) : null, props.allowDrag ? /*#__PURE__*/_jsx(TokenDragPreview, {
      dndPrefix: dndPrefix,
      getTokenProps: index => {
        const token = tokens[index];
        if (!token) return null;
        return {
          value: token.value,
          icon: token.icon,
          tooltip: token.tooltip,
          menuSections: token.menuSections,
          includeDropdown: !!token.menuSections,
          readOnly: token.readOnly
        };
      }
    }) : null]
  });
});
const getTokenInputThemeTokens = (dt, opts) => {
  const tokenDensity = opts?.density ?? 'medium';
  const inputTokens = getInputDesignTokens(dt, {
    disabled: opts?.disabled,
    isCustomFocusTurnedOn: false,
    density: tokenDensity,
    status: opts?.status
  });
  return {
    bd: opts?.disabled ? inputTokens['--input-disabled-bd'] : inputTokens['--input-bd'],
    bg: opts?.disabled ? inputTokens['--input-disabled-bg'] : inputTokens['--input-bg'],
    fg: opts?.disabled ? inputTokens['--input-disabled-fg'] : inputTokens['--input-fg'],
    'active-bd': opts?.status === 'warning' ? dt.tokens['color-action-border-bottom-control-warning'] : opts?.status === 'alert' ? dt.tokens['color-action-border-bottom-control-alert'] : dt.tokens['color-action-border-bottom-control-selected'],
    'active-bg': dt.tokens['color-action-background-control-selected'],
    'spacing-xs': dt.tokens['space-base-xs'],
    'hover-bg': dt.tokens['color-action-background-control-hover'],
    'hover-bd': dt.tokens['color-action-border-bottom-control-hover'],
    'bd-color': opts?.disabled ? dt.tokens['color-action-border-standard-disabled'] : opts?.status === 'warning' ? dt.tokens['color-action-border-bottom-control-warning'] : opts?.status === 'alert' ? dt.tokens['color-action-border-bottom-control-alert'] : dt.tokens['color-action-border-bottom-control-normal'],
    'icon-position-top': '.45rem',
    'bd-color-active': opts?.status === 'warning' ? dt.tokens['color-action-border-bottom-control-warning'] : opts?.status === 'alert' ? dt.tokens['color-action-border-bottom-control-alert'] : dt.tokens['color-action-border-bottom-control-selected'],
    'bd-width': '0 0 1px 0',
    ...(!opts?.isNewTokenValueFlagEnabled && {
      'bd-width': dt.tokens['size-border-width-s']
    }),
    'bottom-bd-anim-dur': dt.tokens['duration-300'],
    'bottom-bd-anim-tm-ease-in': dt.tokens['ease-in'],
    'bottom-bd-anim-tm-ease-out': dt.tokens['ease-out']
  };
};
const getIconProps = suggestionItem => {
  const {
    icon,
    iconTooltip,
    iconAriaLabel
  } = suggestionItem ?? {};
  return icon ? {
    icon,
    tooltip: iconTooltip,
    ariaLabel: iconAriaLabel
  } : undefined;
};
//# sourceMappingURL=TokenInput.js.map